import { map } from '../../../app-core/lib/util/map'
import { IDB_posts, IDB_syncParamsPosts, IDB_update } from '../IDB/IDB.dexie'
import { isReportPost, Post_appPostFromSync, Post_appPostToSync, type Post_TPostId } from './Post.TPost'
import { Serv_syncPosts, type ServSyncPostsResponse } from '../Serv/Serv.posts'
import { Sync_standardSync } from '../Sync/Sync.standardSync'
import Errors from '../../../shared/domain/errors.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { compact } from '../../../shared/lib/array.no-deps'

/**
 * Post.sync
 *
 * Used to sync posts. Posts that have been created, edited or deleted are sent
 * up to the server and the server will send back down any updates. It will also
 * send down any failures that should just be deleted because they will only fail
 * again. However soft deletes will come down with the updates through the isDeleted
 * property.
 *
 * The server will also send down the time and teams that the user belongs to. These
 * will be stored in indexeddb so that they can be sent back up again for the next
 * request.
 *
 * The time is used so that the server only has to send down the changes since the
 * last sync. Though this will be capped so that we do not send down too much data.
 *
 * If the teams that the user is assigned to changes, the plan is to do whole reset.
 * This means deleting all the current posts stored in indexeddb and the server
 * will send down all the posts it can, under the fixed cap.
 *
 * During the sync, a promise will be used to lock any edits or deletes from being
 * overwritten if they are made at the wrong time.
 */
export async function Post_sync({
    returnPostsForTheseUnitIds,
    requireUnitEventPosts,
    postsLastSyncTime,
}: {
    returnPostsForTheseUnitIds?: number[]
    requireUnitEventPosts?: boolean
    postsLastSyncTime?: Date
} = {}): Promise<void> {
    await Sync_standardSync<ServSyncPostsResponse>({
        params: IDB_syncParamsPosts(),
        sync: async (syncParams) => {
            const posts = await getPendingPosts()
            const lastSyncTime = postsLastSyncTime ?? syncParams.lastSyncTime
            // Exclude file posts from sync as they are deprecated.
            return Serv_syncPosts(posts, {
                ...syncParams,
                lastSyncTime,
                unitIds: returnPostsForTheseUnitIds,
                excludeFiles: true,
                includeLanguage: true,
                requireUnitEventPosts,
            })
        },
        write: async (response) => {
            await flagFailures(response.failures)
            const posts = pipe(response.posts, map(Post_appPostFromSync), compact)
            await IDB_posts().bulkPut(posts)
            await IDB_posts().bulkDelete(response.postIdsToDelete as Post_TPostId[])
        },
        clear: () => IDB_posts().clear(),
    })
}

async function getPendingPosts() {
    return (
        IDB_posts()
            .where({ status: 'pendingSync' })
            // Filter report posts as they are synced with reports
            .filter((post) => !isReportPost(post))
            .toArray()
            .then(map(Post_appPostToSync))
            .then(
                map((post) => {
                    delete post.commentCount
                    return post
                })
            )
    )
}

async function flagFailures(failures: ServSyncPostsResponse['failures']) {
    await Promise.all(
        failures
            .filter((failure) => failure.reason !== Errors.post.outdated)
            .map((failure) => failure.id as Post_TPostId)
            .map((id) => IDB_update(IDB_posts(), id, { status: 'syncFailed' }))
    )
}
